import { LanguageType } from "@/components/CodeEditor/types";

const API_KEY_PLACEHOLDER = "<API_KEY>";

/** Seed prompt created in every workspace at signup (see backend/app/utils/init_data.py). */
export const SIGNUP_EXAMPLE_PROMPT_NAME = "Example: page_title";

const SIGNUP_EXAMPLE_INPUT_VARIABLES = {
  topic: "Travel",
  article: "A curated list of 10 awe-inspiring travel destinations every adventurer should visit.",
} as const;

const formatApiKey = (apiKey: string | null) => apiKey ?? API_KEY_PLACEHOLDER;

const hasApiKey = (apiKey: string | null): apiKey is string =>
  Boolean(apiKey && apiKey !== API_KEY_PLACEHOLDER);

const getApiKeyInstructions = (apiKey: string | null) => {
  if (hasApiKey(apiKey)) {
    return `Workspace API key (treat like a password — use env/secrets, never commit):
${apiKey}`;
  }

  return `Workspace API key: not provided yet.

Before changing any code, ask the user for their PromptLayer workspace API key. Tell them to:
1. Return to the PromptLayer onboarding tab
2. Generate an API key in step 1 ("Get your API key")
3. Paste the key here so you can wire it up via env/secrets (never commit it)

Do not proceed with instrumentation until the user provides a real API key. Do not use the \`${API_KEY_PLACEHOLDER}\` placeholder in code.`;
};

export const getRequestInstallSnippet = (language: LanguageType) => {
  if (language === "PYTHON") {
    return "pip install promptlayer";
  }

  return "npm install promptlayer";
};

export const getRequestRunSnippet = (language: LanguageType, apiKey: string | null) => {
  const key = formatApiKey(apiKey);

  if (language === "PYTHON") {
    return `from promptlayer import PromptLayer

pl_client = PromptLayer(api_key="${key}")

# Uses the example prompt created in your workspace at signup.
# Requires OPENAI_API_KEY in your environment — the prompt runs against gpt-4o.
response = pl_client.run(
    prompt_name="${SIGNUP_EXAMPLE_PROMPT_NAME}",
    input_variables={
        "topic": "${SIGNUP_EXAMPLE_INPUT_VARIABLES.topic}",
        "article": "${SIGNUP_EXAMPLE_INPUT_VARIABLES.article}",
    },
)

print(response["request_id"])`;
  }

  return `import { PromptLayer } from "promptlayer";

const pl_client = new PromptLayer({ apiKey: "${key}" });

// Uses the example prompt created in your workspace at signup.
// Requires OPENAI_API_KEY in your environment — the prompt runs against gpt-4o.
const response = await pl_client.run({
  promptName: "${SIGNUP_EXAMPLE_PROMPT_NAME}",
  inputVariables: {
    topic: "${SIGNUP_EXAMPLE_INPUT_VARIABLES.topic}",
    article: "${SIGNUP_EXAMPLE_INPUT_VARIABLES.article}",
  },
});

console.log(response.request_id);`;
};

export const getRequestAgentPrompt = (apiKey: string | null) => {
  const pythonSnippet = getRequestRunSnippet("PYTHON", apiKey);
  const jsSnippet = getRequestRunSnippet("JAVASCRIPT", apiKey);

  return `Instrument this repo so PromptLayer receives at least one logged LLM request.

Goal: the PromptLayer onboarding screen should flip to "request received." Prefer wiring into an existing LLM call path over adding a throwaway demo.

Before you change anything, tell the user what you plan to do:
- Detect Python vs JS/TS and install \`promptlayer\`
- ${hasApiKey(apiKey) ? "Store the workspace API key via env/secrets (never commit it)" : "Ask the user for their PromptLayer workspace API key before writing any code"}
- Run the workspace's existing example prompt (\`${SIGNUP_EXAMPLE_PROMPT_NAME}\`) via \`pl_client.run()\`, or wire an existing LLM path through PromptLayer
- Run once and confirm a \`request_id\` comes back

${getApiKeyInstructions(apiKey)}

1. Detect Python vs JS/TS and install \`promptlayer\` with the project's package manager.
2. ${hasApiKey(apiKey) ? "Create a PromptLayer client with the key above (or \\`PROMPTLAYER_API_KEY\\`)." : "Wait for the user to provide their workspace API key, then create a PromptLayer client with it (or \\`PROMPTLAYER_API_KEY\\`)."}
3. Log a request via \`pl_client.run()\` using the example prompt that already exists in this workspace from signup. Reference:

Python:
\`\`\`python
${pythonSnippet}
\`\`\`

JS/TS:
\`\`\`javascript
${jsSnippet}
\`\`\`

4. If the app already calls a provider SDK directly, you can route that traffic through PromptLayer instead — but the snippet above should work out of the box with \`OPENAI_API_KEY\` set.
5. Run once and confirm you get a \`request_id\` back with no auth errors.

If this repo isn't Python or JS/TS, skip the SDK and log requests directly via the PromptLayer REST API: https://docs.promptlayer.com/reference/introduction

Keep the diff small. Docs: https://docs.promptlayer.com/sdks/python · https://docs.promptlayer.com/sdks/javascript

When you're done, tell the user their next steps:
1. Return to the PromptLayer onboarding tab and wait until the status shows the request was received.
2. Click through to open the request in PromptLayer.
3. From there they can search requests, attach metadata, and start evaluations.`;
};

export const getTraceAgentPrompt = ({
  apiKey,
  providerTitle,
  docsUrl,
  snippet,
}: {
  apiKey: string | null;
  providerTitle: string;
  docsUrl: string;
  snippet: string;
}) =>
  `Instrument this repo so PromptLayer receives at least one ${providerTitle} trace.

Goal: the PromptLayer onboarding screen should flip to "trace received." Reuse the app's real agent/LLM entrypoint when possible.

Before you change anything, tell the user what you plan to do:
- Confirm ${providerTitle} fits this stack
- Install any packages the setup needs
- ${hasApiKey(apiKey) ? "Configure the workspace API key via env/secrets (never commit it)" : "Ask the user for their PromptLayer workspace API key before writing any code"}
- Apply the ${providerTitle} instrumentation in the real startup / agent path
- Run once and confirm a trace can be sent

${getApiKeyInstructions(apiKey)}

1. Confirm ${providerTitle} fits this stack (or the closest supported equivalent).
2. Install any packages the snippet needs with the project's package manager.
3. ${hasApiKey(apiKey) ? "Apply this setup" : "Once the user provides their workspace API key, apply this setup"} in the right startup / agent file, adapted to local style:

\`\`\`
${snippet}
\`\`\`

4. If the snippet doesn't fit cleanly, follow ${docsUrl} and finish with the closest PromptLayer + ${providerTitle} pattern.
5. Run the path once. Flush/shutdown exporters if required. Confirm no auth/exporter errors.

If this repo isn't Python or JS/TS, skip the SDK and send traces directly via the PromptLayer REST API: https://docs.promptlayer.com/reference/introduction

Keep the diff small — export to PromptLayer, don't rip out existing telemetry unless it conflicts.
Docs: ${docsUrl}

When you're done, tell the user their next steps:
1. Return to the PromptLayer onboarding tab and wait until the status shows the trace was received.
2. Click through to open the trace in PromptLayer.
3. From there they can inspect the span waterfall and related request logs.`;
